import React, { useEffect, useMemo, useRef, useState } from 'react'
import { getAsset } from '../services/assets'
import { fetchHeros, fetchZones, OkMainHero, OkMainZone } from '../services/okMainApi'
import { GameState } from '../types'
import ZoneInfoModal from './ZoneInfoModal'
import veriskAvatar from '../assets/custom/VeriskAvatar.png'
import erokAvatar from '../assets/custom/Erok-BuruAvatar.png'
import vergAvatar from '../assets/custom/VergAvatar.png'
import astridAvatar from '../assets/custom/AstridAvatar.png'
import nyrixAvatar from '../assets/custom/NyrixAvatar.png'
import shanuAvatar from '../assets/custom/ShanuAvatar.png'
import lorianAvatar from '../assets/custom/LorianAvatar.png'
import gemataAvatar from '../assets/custom/GemataAvatar.png'
import axelAvatar from '../assets/custom/AxelAvatar.png'
import ZoneBorderParticles from './ZoneBorderParticles'
import { Language, t } from '../data/translations'

const zones = [
  { id: 3, x: 273, y: 0, w: 205, h: 217 },
  { id: 5, x: 369, y: 35, w: 216, h: 271 },
  { id: 10, x: 157, y: 36, w: 229, h: 275 },
  { id: 11, x: 476, y: 78, w: 220, h: 256 },
  { id: 13, x: 58, y: 116, w: 243, h: 188 },
  { id: 6, x: 418, y: 253, w: 330, h: 132 },
  { id: 9, x: 0, y: 270, w: 326, h: 122 },
  { id: 1, x: 479, y: 327, w: 216, h: 215 },
  { id: 2, x: 59, y: 327, w: 211, h: 214 },
  { id: 7, x: 371, y: 353, w: 221, h: 272 },
  { id: 8, x: 166, y: 342, w: 210, h: 279 },
  { id: 12, x: 269, y: 419, w: 217, h: 203 },
  { id: 4, x: 323, y: 200, w: 109, h: 181 },
]

const files: Record<string, any> = (import.meta as any).glob('../MapLayers/**/*', { eager: true })
const layerUrl: Record<string, string> = {}
for (const p in files) {
  const u = (files[p] as any)?.default as string
  if (u) layerUrl[p] = u
}

function getLayer(id: number): string | null {
  const p = `../MapLayers/Neutral/${id}.png`
  return layerUrl[p] || null
}

const WorldMap: React.FC<{ onBack: () => void; gameState?: GameState | null; isCaptureMode?: boolean; onCapture?: (zoneId: number, heroId?: number) => void; language: Language }> = ({ onBack, gameState, isCaptureMode, onCapture, language }) => {
  const [zonesData, setZonesData] = useState<OkMainZone[] | null>(null)
  const [herosData, setHerosData] = useState<OkMainHero[] | null>(null)
  const [selected, setSelected] = useState<{ zoneId: number; factionName: string; hero: { name: string; imageUrl?: string } | null } | null>(null)
  const [loading, setLoading] = useState(true)
  const [hoveredZoneId, setHoveredZoneId] = useState<number | null>(null)

  useEffect(() => {
    const load = () => {
      Promise.allSettled([fetchZones(), fetchHeros()]).then(([z, h]) => {
        if (z.status === 'fulfilled') setZonesData(z.value)
        if (h.status === 'fulfilled') setHerosData(h.value)
        setLoading(false)
      })
    }
    load()
  }, [])

  const bg = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_MAP_BASE_IMG_URL) || getAsset('ui.table.bgA')


  // Helper to get points for a zone from server data
  const getZonePoints = (zId: number) => {
    const z = zonesData?.find(zz => zz.id === zId)
    if (z) {
      return {
        harmonized: z.point1 || 0,
        chaotic: z.point2 || 0,
        untouched: z.point3 || 0
      }
    }
    return undefined
  }


  const imageDataCache = useRef<Record<string, ImageData>>({})
  const imgCacheRef = useRef<Record<string, HTMLImageElement>>({})

  useEffect(() => {
    const toLoad: string[] = []
    zones.forEach(z => {
      const p = getLayer(z.id)
      if (p) toLoad.push(p)
    })

    // Helper to extract image data
    const extractImageData = (img: HTMLImageElement, src: string) => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d', { willReadFrequently: true })
      if (ctx) {
        ctx.drawImage(img, 0, 0)
        try {
          imageDataCache.current[src] = ctx.getImageData(0, 0, img.width, img.height)
        } catch (e) {
          console.warn('Could not get image data for', src, e)
        }
      }
    }

    toLoad.forEach(src => {
      if (!imgCacheRef.current[src]) {
        const im = new Image()
        im.crossOrigin = "Anonymous" // Needed if assets are external
        im.onload = () => {
          extractImageData(im, src)
        }
        im.src = src
        imgCacheRef.current[src] = im
      } else if (!imageDataCache.current[src]) {
        // If already in cache but no image data (unlikely but safe)
        const im = imgCacheRef.current[src]
        if (im.complete) {
          extractImageData(im, src)
        } else {
          im.onload = () => extractImageData(im, src)
        }
      }
    })
  }, [])

  const heroById = React.useMemo(() => {
    const map: Record<number, OkMainHero> = {}
      ; (herosData || []).forEach(h => { map[h.id] = h })
    return map
  }, [herosData])

  const zoneHeroIdByZoneId = React.useMemo(() => {
    const map: Record<number, number | undefined> = {}
      ; (zonesData || []).forEach(z => { map[z.id] = z.heros?.[0]?.heroId })
    return map
  }, [zonesData])



  const fallbackHeroForZone = (zoneId: number): { name: string; imageUrl: string } | null => {
    if (zoneId === 1) return { name: 'Verisk', imageUrl: veriskAvatar }
    if (zoneId === 8) return { name: 'Verg', imageUrl: vergAvatar }
    if (zoneId === 3) return { name: 'Astrid', imageUrl: astridAvatar }
    if (zoneId === 12) return { name: 'Nyrix', imageUrl: nyrixAvatar }
    if (zoneId === 9) return { name: 'Shanu', imageUrl: shanuAvatar }
    if (zoneId === 6) return { name: 'Lorian', imageUrl: lorianAvatar }
    if (zoneId === 10) return { name: 'Gemata', imageUrl: gemataAvatar }
    if (zoneId === 5) return { name: 'Axel', imageUrl: axelAvatar }
    return null
  }

  const [mapScale, setMapScale] = useState(1)

  useEffect(() => {
    const updateScale = () => {
      // 750 width, ~638 height. Adding a small margin
      const scaleX = window.innerWidth / 760;
      const scaleY = window.innerHeight / 660;
      setMapScale(Math.min(1, scaleX, scaleY));
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [])

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = (e.clientX - rect.left) / mapScale
    const y = (e.clientY - rect.top) / mapScale

    // Check zones in reverse order (top to bottom)
    let foundZoneId: number | null = null

    for (let i = zones.length - 1; i >= 0; i--) {
      const z = zones[i]
      // Simple bounding box check first
      if (x >= z.x && x < z.x + z.w && y >= z.y && y < z.y + z.h) {
        const imgUrl = getLayer(z.id)

        if (imgUrl && imageDataCache.current[imgUrl]) {
          const data = imageDataCache.current[imgUrl]
          // Map global x,y to local image coordinates
          // Note: The image is displayed at z.w, z.h. The imageData might be different natural size?
          // Usually we draw the image to fit z.w/z.h. 
          // If the natural size is different, we need to scale coordinates.
          const img = imgCacheRef.current[imgUrl]
          if (img) {
            const localX = Math.floor((x - z.x) * (img.naturalWidth / z.w))
            const localY = Math.floor((y - z.y) * (img.naturalHeight / z.h))

            if (localX >= 0 && localX < img.naturalWidth && localY >= 0 && localY < img.naturalHeight) {
              const index = (localY * img.naturalWidth + localX) * 4
              const alpha = data.data[index + 3]
              if (alpha > 20) { // Threshold for transparency
                foundZoneId = z.id
                break // Found the top-most zone
              }
            }
          }
        } else if (!imgUrl) {
          // Fallback for no image (shouldn't happen often based on logic, but for safety)
          foundZoneId = z.id
          break
        }
      }
    }

    setHoveredZoneId(foundZoneId)
  }

  const handleClickWithZone = (zoneId: number) => {
    if (isCaptureMode && onCapture) {
      const z = zones.find(z => z.id === zoneId)
      const heroId = z ? zoneHeroIdByZoneId[z.id] : undefined
      onCapture(zoneId, heroId);
      return;
    }
    const z = zones.find(z => z.id === zoneId)
    if (z) {
      const factionId = gameState?.mapControl?.[z.id] ?? zonesData?.find(zz => zz.id === z.id)?.faction ?? (z.id === 1 ? 1 : z.id === 2 ? 2 : z.id === 3 ? 3 : 0)
      const factionName = factionId === 1 ? 'Harmonized' : factionId === 2 ? 'Chaotic' : factionId === 3 ? 'Untouched' : 'Neutral'

      const heroId = zoneHeroIdByZoneId[z.id]
      let heroImg = heroId ? heroById[heroId]?.imageUrl || null : null
      let heroName = heroId ? heroById[heroId]?.name : undefined
      if (!heroId || !heroImg) {
        const fb = fallbackHeroForZone(z.id)
        if (fb) { heroImg = fb.imageUrl; heroName = fb.name }
      }

      const localizedHeroName = heroName ? t(`hero.${heroName.toUpperCase().replace(/[\s-]/g, '_')}.name`, language) : undefined
      const hero = localizedHeroName ? { name: localizedHeroName, originalName: heroName, imageUrl: heroImg || undefined } : null
      setSelected({ zoneId: z.id, factionName, hero })
    }
  }

  return (
    <div className="w-full h-screen relative overflow-hidden" style={{ backgroundImage: `url(${bg})`, backgroundSize: 'cover' }}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="absolute top-4 left-[116px] z-10 w-64">
        <button
          className="w-full min-h-[54px] text-white font-bold text-lg py-3 px-6 rounded-lg transition-all duration-300 shadow-lg transform hover:scale-105"
          style={{ backgroundImage: `url(${getAsset('ui.menu.btnPrimary')})`, backgroundSize: '100% 100%', backgroundPosition: 'center' }}
          onClick={onBack}
        >
          {t('ui.back', language)}
        </button>
      </div>

      {/* Map Container */}
      <div
        className="absolute left-1/2 top-1/2"
        style={{ width: 750, height: 750 * 0.851, touchAction: 'none', transform: `translate(-50%, -50%) scale(${mapScale})` }}
        onPointerMove={handlePointerMove}
        onClick={(e) => {
          // For touch devices: detect zone at click position
          const rect = e.currentTarget.getBoundingClientRect();
          const x = (e.clientX - rect.left) / mapScale;
          const y = (e.clientY - rect.top) / mapScale;

          let clickedZoneId: number | null = hoveredZoneId;

          // Find zone at click position
          for (let i = zones.length - 1; i >= 0; i--) {
            const z = zones[i];
            if (x >= z.x && x < z.x + z.w && y >= z.y && y < z.y + z.h) {
              const imgUrl = getLayer(z.id);
              if (imgUrl && imageDataCache.current[imgUrl]) {
                const data = imageDataCache.current[imgUrl];
                const img = imgCacheRef.current[imgUrl];
                if (img) {
                  const localX = Math.floor((x - z.x) * (img.naturalWidth / z.w));
                  const localY = Math.floor((y - z.y) * (img.naturalHeight / z.h));
                  if (localX >= 0 && localX < img.naturalWidth && localY >= 0 && localY < img.naturalHeight) {
                    const index = (localY * img.naturalWidth + localX) * 4;
                    const alpha = data.data[index + 3];
                    if (alpha > 20) {
                      clickedZoneId = z.id;
                      break;
                    }
                  }
                }
              }
            }
          }

          if (clickedZoneId !== null) {
            setHoveredZoneId(clickedZoneId);
            handleClickWithZone(clickedZoneId);
          }
        }}
        onPointerLeave={() => setHoveredZoneId(null)}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-white">{t('ui.loadingMap', language)}...</div>
        )}
        {isCaptureMode && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/60 text-yellow-400 px-4 py-2 rounded-lg font-bold text-xl z-50 animate-pulse border border-yellow-500/50">
            {t('ui.chooseZoneToCapture', language)}
          </div>
        )}
        {zones.map(z => {
          let factionId = gameState?.mapControl?.[z.id] ?? zonesData?.find(zz => zz.id === z.id)?.faction ?? 0
          // Enforce home zones if neutral
          if (factionId === 0) {
            if (z.id === 1) factionId = 1
            else if (z.id === 2) factionId = 2
            else if (z.id === 3) factionId = 3
          }

          const factionName = factionId === 1 ? 'Harmonized' : factionId === 2 ? 'Chaotic' : factionId === 3 ? 'Untouched' : 'Neutral'
          const img = getLayer(z.id)
          const ring = getAsset('avatars.frame')
          const heroId = zoneHeroIdByZoneId[z.id]
          let heroImg = heroId ? heroById[heroId]?.imageUrl || null : null

          if (!heroId || !heroImg) {
            const fb = fallbackHeroForZone(z.id)
            if (fb) { heroImg = fb.imageUrl }
          }

          const isHovered = hoveredZoneId === z.id

          // Faction-colored glow on hover only (when zone is captured)
          let glowFilter = 'none'
          if (isHovered && factionId !== 0) {
            if (factionId === 3) glowFilter = 'drop-shadow(0 0 15px rgba(255, 255, 255, 0.9))' // Untouched - White
            else if (factionId === 2) glowFilter = 'drop-shadow(0 0 15px rgba(220, 20, 60, 0.9))' // Chaotic - Dark Red
            else if (factionId === 1) glowFilter = 'drop-shadow(0 0 15px rgba(56, 189, 248, 0.9))' // Harmonized - Sky Blue
          }

          return (
            <div
              key={z.id}
              className={`absolute transition-transform duration-200 ease-out ${isHovered ? 'z-20' : 'z-10'}`}
              style={{
                left: z.x,
                top: z.y,
                width: z.w,
                height: z.h,
                transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                pointerEvents: 'none' // Let the container handle events
              }}
            >
              {img && <img src={img} alt={`Zone ${z.id}`} className="absolute inset-0 w-full h-full object-cover transition-all duration-200" style={{ filter: glowFilter }} />}
              {!img && <div className="absolute inset-0 rounded-lg border border-sky-400/40" />}

              {/* Fantasy Particles on Hover */}
              {isHovered && factionId !== 0 && img && (
                <ZoneBorderParticles
                  width={z.w}
                  height={z.h}
                  imageUrl={img}
                  color={factionId === 2 ? '#DC143C' : factionId === 1 ? '#38BDF8' : '#FFFFFF'}
                  factionId={factionId}
                />
              )}

              {/* Zone Label */}
              <div className={`absolute top-2 left-2 px-2 py-1 bg-black/40 text-white text-xs rounded pointer-events-none transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>{t('term.zone', language)} {z.id}</div>

              {/* Icons Container - Fades in on hover */}
              <div className={`absolute inset-0 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
                {/* Faction Icon for Zones 1, 2, 3 */}
                {[1, 2, 3].includes(z.id) && factionId !== 0 && (
                  <div className="absolute pointer-events-none" style={{ left: z.w / 2 - 40, top: z.h / 2 - 40, width: 80, height: 80 }}>
                    <img
                      src={factionId === 1 ? new URL('../assets/custom/Harmonized.png', import.meta.url).href :
                        factionId === 2 ? new URL('../assets/custom/Chaootic.png', import.meta.url).href :
                          new URL('../assets/custom/Untouched.png', import.meta.url).href}
                      alt="faction"
                      className="w-full h-full object-contain drop-shadow-[0_0_10px_rgba(255,255,255,0.5)]"
                    />
                  </div>
                )}

                {/* Hero Avatar (Only for non-home zones or if specific logic requires) */}
                {heroImg && (factionId !== 0 || z.id === 5 || z.id === 8 || z.id === 12 || z.id === 9 || z.id === 6 || z.id === 10) && ![1, 2, 3].includes(z.id) && (
                  <div className="absolute pointer-events-none" style={{ left: z.w / 2 - 52, top: z.h / 2 - 52, width: 104, height: 104 }}>
                    <img src={heroImg} alt="hero" className="absolute left-1 top-1 w-24 h-24 rounded-full object-cover" />
                    <img src={ring} alt="ring" className="absolute inset-0 w-full h-full" />
                  </div>
                )}
              </div>

              {/* Siege Timer for Zone 4 */}
              {z.id === 4 && (() => {
                const zData = zonesData?.find(zz => zz.id === 4);
                if (!zData || !zData.controlStartDate || !zData.eventHeroId || zData.faction === 0) return null;

                const FOUR_DAYS_MS = 4 * 24 * 3600 * 1000;
                const elapsed = Date.now() - zData.controlStartDate;
                const progress = Math.min(100, (elapsed / FOUR_DAYS_MS) * 100);
                const remaining = Math.max(0, FOUR_DAYS_MS - elapsed);

                const hours = Math.floor(remaining / (3600 * 1000));
                const mins = Math.floor((remaining % (3600 * 1000)) / (60 * 1000));

                return (
                  <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 w-32 flex flex-col items-center gap-1 z-30">
                    <div className="text-[10px] font-bold text-yellow-400 drop-shadow-md whitespace-nowrap bg-black/50 px-2 rounded">
                      SIEGE: {hours}h {mins}m
                    </div>
                    <div className="w-full h-2 bg-slate-900 border border-amber-900/50 rounded-full overflow-hidden shadow-lg">
                      <div
                        className="h-full bg-gradient-to-r from-amber-600 to-yellow-400 transition-all duration-1000"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                );
              })()}
            </div>
          )
        })}
        {selected && (
          <ZoneInfoModal
            zoneId={selected.zoneId}
            factionName={selected.factionName}
            hero={selected.hero}
            points={getZonePoints(selected.zoneId)}
            onClose={() => setSelected(null)}
            language={language}
          />
        )}
      </div>
    </div>
  )
}

export default WorldMap
